import { FC } from 'react';
import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import ResponsiveImage from '../ResponsiveImage';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link';

import styles from './LargeItemCarouselItem.scss';

import { SharedFavoritesProps } from './HpSharedHeroModule';
import HeartWrapper from 'dibs-portfolio-heart';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';

import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

const { EVENT_WISHLIST } = eventNameConstants;
const { INTERACTION_TYPE_ITEM_FAVORITING } = interactionTypeConstants;
const { STEP_INTERACTION_ITEM_ADDED, STEP_INTERACTION_ITEM_REMOVED } = stepInteractionConstants;

type Props = {
    item: {
        itemId: string | null;
        imageUrl: string | null;
        title: string | null;
        link: string | null;
    };
    isVisible?: boolean;
    onClick: () => void;
    useLazyLoadImages: boolean;
    userId: string;
    favorites: SharedFavoritesProps['favorites'];
};

const LargeItemCarouselItem: FC<Props> = ({
    item,
    isVisible,
    onClick,
    useLazyLoadImages,
    userId,
    favorites,
}) => {
    const { imageUrl, title, link } = item;

    const authModalShow = (favoriteItem: SharedFavoritesProps['favorites']): void => {
        if (!userId) {
            authModalLoader
                .show({
                    flow: authModalLoader.constants.SAVE_ITEM_FLOW,
                    ga: {
                        label: 'save item - "editorial picks" homepage module',
                    },
                })
                .then(favoriteItem);
        }
    };

    const heroItemClassName = classnames(styles.heroItem, {
        [styles.isVisible]: isVisible,
    });
    return (
        <Link
            dataTn="hero-item-link"
            href={link || ''}
            className={heroItemClassName}
            onClick={onClick}
        >
            <HeartWrapper
                itemId={item.itemId}
                loadPortfolioData={favorites.loadPortfolioData}
                viewer={favorites.viewer || null}
                userId={favorites.userId || null}
                userIds={favorites.userIds || []}
                authModalShow={authModalShow}
                onFavorited={() => {
                    trackEvent(
                        {
                            action: 'item added',
                            label: 'homepage - editors picks',
                            category: 'item favoriting',
                            isInteractiveEvent: true,
                            additional: {
                                itemId: item.itemId,
                            },
                            eventName: EVENT_WISHLIST,
                            interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
                            step_interaction_name: STEP_INTERACTION_ITEM_ADDED,
                        },
                        null
                    );
                }}
                onUnFavorited={() => {
                    trackEvent(
                        {
                            action: 'item removed',
                            label: 'homepage - editors picks',
                            category: 'item favoriting',
                            isInteractiveEvent: true,
                            additional: {
                                itemId: item.itemId,
                            },
                            eventName: EVENT_WISHLIST,
                            interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
                            step_interaction_name: STEP_INTERACTION_ITEM_REMOVED,
                        },
                        null
                    );
                }}
                selectedItemIds={favorites.selectedItemIds}
                theme="dark"
            />

            <HpLazyImageWrapper useLazyLoad={useLazyLoadImages} className={styles.imageWrapper}>
                <ResponsiveImage
                    // no need for alt if title is present bc labeling the img with title is
                    // redundant with title as link text. only use alt if != title.
                    ariaHidden
                    className={styles.image}
                    src={imageUrl || ''}
                    dataTn="hero-item-image"
                    srcSetWidths={[200, 240]}
                />
            </HpLazyImageWrapper>
            <div
                className={styles.title}
                dangerouslySetInnerHTML={{ __html: title || '' }}
                data-tn="hero-item-title"
            />
        </Link>
    );
};

export default LargeItemCarouselItem;
